// frontend/src/pages/RecurringTasks.jsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import AssignTemplatesToClient from '../components/tasks/AssignTemplatesToClient';
import { Icon } from '../components/ui';
import { showConfirm } from '../utils/confirmUtils';
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';

const FREQUENCIES = [
  { value: 'monthly', label: 'Mensuelle' },
  { value: 'quarterly', label: 'Trimestrielle' },
  { value: 'semesterly', label: 'Semestrielle' },
  { value: 'annual', label: 'Annuelle' },
  { value: 'custom', label: 'Personnalisée' },
];

const normalizeId = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value._id || value.id || null;
};

export default function RecurringTasks() {
  // Tabs
  const [activeTab, setActiveTab] = useState('templates'); // 'templates' | 'scheduled'

  // Common data
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);

  // Templates tab
  const [editingId, setEditingId] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);

  // NEW: scope global/client
  const [newTemplate, setNewTemplate] = useState({
    scope: 'client', // 'global' | 'client'
    name: '',
    description: '',
    frequency: 'monthly',
    clientId: '',
    defaultAssignee: '',
    estimatedHours: '',
    category: 'accounting',
    startDate: '',
    endDate: '',
  });

  // Scheduled tab
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientTemplates, setClientTemplates] = useState([]);
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, clientsRes, usersRes] = await Promise.all([
        api.get('/tasks/templates'),
        api.get('/clients'),
        api.get('/users?role=collaborator'),
      ]);

      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setCollaborators(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (error) {
      console.error('Erreur chargement modèles récurrents:', error);
      alert('Erreur lors du chargement des tâches récurrentes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadScheduledForClient = async (clientId) => {
    if (!clientId) {
      setClientTemplates([]);
      setScheduledTasks([]);
      return;
    }

    setLoadingScheduled(true);
    try {
      const [tplRes, tasksRes] = await Promise.all([
        api.get('/tasks/templates', { params: { clientId } }),
        api.get('/tasks', { params: { clientId } }),
      ]);

      const tpls = Array.isArray(tplRes.data) ? tplRes.data : [];
      const tasks = Array.isArray(tasksRes.data) ? tasksRes.data : [];

      // On ne garde que les tâches récurrentes (ou liées à un template)
      const recurringTasks = tasks.filter(
        (t) => t?.taskType === 'recurring' || t?.sourceTemplate
      );

      setClientTemplates(tpls);
      setScheduledTasks(recurringTasks);
    } catch (error) {
      console.error('Erreur chargement programmation:', error);
      alert('Erreur lors du chargement des tâches programmées');
    } finally {
      setLoadingScheduled(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'scheduled') return;
    loadScheduledForClient(selectedClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedClientId]);

  const getClientName = (client) => {
    if (!client) return 'Global';
    if (typeof client === 'object' && (client.companyName || client.name)) {
      return client.companyName || client.name || 'N/A';
    }
    const id = normalizeId(client);
    const c = clients.find((x) => normalizeId(x) === id) || null;
    return c?.companyName || c?.name || 'N/A';
  };

  const getUserName = (user) => {
    if (!user) return '—';
    if (typeof user === 'object' && (user.name || user.email)) {
      return user.name || user.email || 'N/A';
    }
    const id = normalizeId(user);
    const u = collaborators.find((x) => normalizeId(x) === id) || null;
    return u?.name || u?.email || 'N/A';
  };

  const handleFieldChange = (id, field, value) => {
    setTemplates((prev) =>
      prev.map((tpl) => (normalizeId(tpl) === id ? { ...tpl, [field]: value } : tpl))
    );
  };

  const handleToggleActive = async (tpl) => {
    try {
      const updated = { ...tpl, active: !tpl.active };
      await api.put(`/tasks/templates/${normalizeId(tpl)}`, { active: updated.active });
      setTemplates((prev) =>
        prev.map((t) => (normalizeId(t) === normalizeId(tpl) ? updated : t))
      );
    } catch (error) {
      console.error('Erreur activation modèle:', error);
      alert('Erreur lors du changement de statut actif/inactif');
    }
  };

  const handleSave = async (tpl) => {
    try {
      const payload = {
        name: tpl.name,
        description: tpl.description,
        frequency: tpl.frequency,
        clientId: typeof tpl.clientId === 'object' ? normalizeId(tpl.clientId) : tpl.clientId || null,
        defaultAssignee:
          typeof tpl.defaultAssignee === 'object'
            ? normalizeId(tpl.defaultAssignee)
            : tpl.defaultAssignee || null,
        estimatedHours: Number(tpl.estimatedHours) || 0,
        category: tpl.category || 'accounting',
        startDate: tpl.startDate || null,
        endDate: tpl.endDate || null,
      };

      await api.put(`/tasks/templates/${normalizeId(tpl)}`, payload);
      setEditingId(null);
      await loadData();
    } catch (error) {
      console.error('Erreur sauvegarde modèle:', error);
      alert('Erreur lors de la sauvegarde du modèle');
    }
  };

  // ✅ NOUVEAU : suppression d’un template (global ou client)
  const handleDeleteTemplate = async (tpl) => {
    const id = normalizeId(tpl);
    if (!id) return;

    const labelScope = tpl?.clientId ? `client (${getClientName(tpl.clientId)})` : 'global';
    const ok = window.confirm(
      `Supprimer définitivement le modèle "${tpl?.name || ''}" [${labelScope}] ?\n\n⚠️ Action irréversible`
    );
    if (!ok) return;

    try {
      await api.delete(`/tasks/templates/${id}`);
      // si on était en édition de ce template, on sort du mode édition
      setEditingId((prev) => (prev === id ? null : prev));
      await loadData();
    } catch (error) {
      console.error('Erreur suppression modèle:', error);
      alert("❌ Erreur lors de la suppression du modèle");
    }
  };

  const resetNewTemplate = () => {
    setNewTemplate({
      scope: 'client',
      name: '',
      description: '',
      frequency: 'monthly',
      clientId: '',
      defaultAssignee: '',
      estimatedHours: '',
      category: 'accounting',
      startDate: '',
      endDate: '',
    });
  };

  const handleCreate = async () => {
    if (!newTemplate.name) {
      alert('Le nom est obligatoire');
      return;
    }

    const isClientTemplate = newTemplate.scope === 'client';
    if (isClientTemplate && (!newTemplate.clientId || !newTemplate.defaultAssignee)) {
      alert('Pour un modèle client, le client et le collaborateur sont obligatoires');
      return;
    }

    try {
      const payload = {
        name: newTemplate.name,
        description: newTemplate.description,
        frequency: newTemplate.frequency,
        clientId: isClientTemplate ? newTemplate.clientId : null,
        defaultAssignee: isClientTemplate ? newTemplate.defaultAssignee : null,
        estimatedHours: Number(newTemplate.estimatedHours) || 0,
        category: newTemplate.category || 'accounting',
        active: true,
        startDate: newTemplate.startDate || null,
        endDate: newTemplate.endDate || null,
      };

      await api.post('/tasks/templates', payload);
      resetNewTemplate();
      await loadData();
    } catch (error) {
      console.error('Erreur création modèle:', error);
      alert('Erreur lors de la création du modèle');
    }
  };

  const selectedClientObj = useMemo(() => {
    const id = selectedClientId;
    return clients.find((c) => normalizeId(c) === id) || null;
  }, [clients, selectedClientId]);

  const globalTemplates = useMemo(
    () => templates.filter((t) => !t.clientId),
    [templates]
  );
  const perClientTemplates = useMemo(
    () => templates.filter((t) => !!t.clientId),
    [templates]
  );

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-bold mb-4">Tâches récurrentes</h1>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header + Tabs */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold">Tâches récurrentes</h1>
          <p className="text-gray-600 text-sm">
            Gestion des modèles et visualisation des tâches programmées par client.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 rounded border ${
              activeTab === 'templates' ? 'bg-blue-600 text-white' : 'bg-white'
            }`}
          >
            Modèles
          </button>
          <button
            onClick={() => setActiveTab('scheduled')}
            className={`px-4 py-2 rounded border ${
              activeTab === 'scheduled' ? 'bg-blue-600 text-white' : 'bg-white'
            }`}
          >
            Tâches programmées
          </button>
        </div>
      </div>

      {/* TAB: Templates */}
      {activeTab === 'templates' && (
        <>
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => setShowAssignModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Programmer depuis templates globaux
            </button>
          </div>

          {/* Create form */}
          <div className="mb-8 border rounded-lg p-4 bg-white shadow">
            <h2 className="font-semibold mb-4">Nouveau modèle</h2>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={newTemplate.scope}
                  onChange={(e) => {
                    const scope = e.target.value;
                    setNewTemplate((p) => ({
                      ...p,
                      scope,
                      clientId: scope === 'global' ? '' : p.clientId,
                      defaultAssignee: scope === 'global' ? '' : p.defaultAssignee,
                    }));
                  }}
                >
                  <option value="global">Modèle global</option>
                  <option value="client">Modèle client</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Nom</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Déclaration TVA mensuelle"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Fréquence</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={newTemplate.frequency}
                  onChange={(e) => setNewTemplate((p) => ({ ...p, frequency: e.target.value }))}
                >
                  {FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Client</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={newTemplate.clientId}
                  disabled={newTemplate.scope === 'global'}
                  onChange={(e) => setNewTemplate((p) => ({ ...p, clientId: e.target.value }))}
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map((c) => (
                    <option key={normalizeId(c)} value={normalizeId(c)}>
                      {c.companyName || c.name || 'Client'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Collaborateur</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={newTemplate.defaultAssignee}
                  disabled={newTemplate.scope === 'global'}
                  onChange={(e) =>
                    setNewTemplate((p) => ({ ...p, defaultAssignee: e.target.value }))
                  }
                >
                  <option value="">Sélectionner</option>
                  {collaborators.map((u) => (
                    <option key={normalizeId(u)} value={normalizeId(u)}>
                      {u.name || u.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Heures estimées</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  className="w-full border rounded px-2 py-1"
                  value={newTemplate.estimatedHours}
                  onChange={(e) =>
                    setNewTemplate((p) => ({ ...p, estimatedHours: e.target.value }))
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Date de lancement</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={newTemplate.startDate}
                  onChange={(e) => setNewTemplate((p) => ({ ...p, startDate: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Échéance max (optionnelle)</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={newTemplate.endDate}
                  onChange={(e) => setNewTemplate((p) => ({ ...p, endDate: e.target.value }))}
                />
              </div>

              <div className="md:col-span-5">
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newTemplate.description}
                  onChange={(e) =>
                    setNewTemplate((p) => ({ ...p, description: e.target.value }))
                  }
                  placeholder="Détails, documents à préparer, etc."
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={resetNewTemplate}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Réinitialiser
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Ajouter le modèle
              </button>
            </div>
          </div>

          {/* Lists */}
          <div className="space-y-6">
            {/* GLOBAL */}
            <div className="bg-white border rounded-lg shadow overflow-x-auto">
              <div className="p-3 border-b font-semibold">Modèles globaux</div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-3 border text-left">Nom</th>
                    <th className="p-3 border text-left">Fréquence</th>
                    <th className="p-3 border text-left">Actif</th>
                    <th className="p-3 border text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {globalTemplates.map((tpl) => {
                    const isEditing = editingId === normalizeId(tpl);
                    return (
                      <tr key={normalizeId(tpl)} className="hover:bg-gray-50">
                        <td className="p-3 border">
                          {isEditing ? (
                            <input
                              className="w-full border rounded px-2 py-1"
                              value={tpl.name || ''}
                              onChange={(e) =>
                                handleFieldChange(normalizeId(tpl), 'name', e.target.value)
                              }
                            />
                          ) : (
                            <>
                              <div className="font-medium">{tpl.name}</div>
                              <div className="text-xs text-gray-500">{tpl.description}</div>
                            </>
                          )}
                        </td>

                        <td className="p-3 border">
                          {isEditing ? (
                            <select
                              className="w-full border rounded px-2 py-1"
                              value={tpl.frequency || 'monthly'}
                              onChange={(e) =>
                                handleFieldChange(normalizeId(tpl), 'frequency', e.target.value)
                              }
                            >
                              {FREQUENCIES.map((f) => (
                                <option key={f.value} value={f.value}>
                                  {f.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            FREQUENCIES.find((f) => f.value === tpl.frequency)?.label ||
                            tpl.frequency
                          )}
                        </td>

                        <td className="p-3 border">
                          <button
                            onClick={() => handleToggleActive(tpl)}
                            className={`px-2 py-1 rounded text-xs ${
                              tpl.active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {tpl.active ? 'Actif' : 'Inactif'}
                          </button>
                        </td>

                        <td className="p-3 border">
                          {isEditing ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSave(tpl)}
                                className="px-3 py-1 bg-blue-600 text-white text-xs rounded"
                              >
                                Enregistrer
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="px-3 py-1 bg-gray-300 text-xs rounded"
                              >
                                Annuler
                              </button>

                              {/* 🗑️ Supprimer */}
                              <button
                                onClick={() => handleDeleteTemplate(tpl)}
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded"
                                title="Supprimer"
                              >
                                <Icon name="IconTrash" /> Supprimer
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingId(normalizeId(tpl))}
                                className="px-3 py-1 bg-white border text-xs rounded hover:bg-gray-100"
                              >
                                Modifier
                              </button>

                              {/* 🗑️ Supprimer */}
                              <button
                                onClick={() => handleDeleteTemplate(tpl)}
                                className="px-3 py-1 bg-white border border-red-300 text-red-700 text-xs rounded hover:bg-red-50"
                                title="Supprimer"
                              >
                                <Icon name="IconTrash" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {globalTemplates.length === 0 && (
                    <tr>
                      <td className="p-4 text-center text-gray-500" colSpan={4}>
                        Aucun modèle global.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* CLIENT */}
            <div className="bg-white border rounded-lg shadow overflow-x-auto">
              <div className="p-3 border-b font-semibold">Modèles client</div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-3 border text-left">Nom</th>
                    <th className="p-3 border text-left">Client</th>
                    <th className="p-3 border text-left">Collaborateur</th>
                    <th className="p-3 border text-left">Fréquence</th>
                    <th className="p-3 border text-left">Début</th>
                    <th className="p-3 border text-left">Fin</th>
                    <th className="p-3 border text-left">Heures</th>
                    <th className="p-3 border text-left">Actif</th>
                    <th className="p-3 border text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {perClientTemplates.map((tpl) => {
                    const isEditing = editingId === normalizeId(tpl);
                    return (
                      <tr key={normalizeId(tpl)} className="hover:bg-gray-50">
                        <td className="p-3 border">
                          {isEditing ? (
                            <input
                              className="w-full border rounded px-2 py-1"
                              value={tpl.name || ''}
                              onChange={(e) =>
                                handleFieldChange(normalizeId(tpl), 'name', e.target.value)
                              }
                            />
                          ) : (
                            <>
                              <div className="font-medium">{tpl.name}</div>
                              <div className="text-xs text-gray-500">{tpl.description}</div>
                            </>
                          )}
                        </td>

                        <td className="p-3 border">
                          {isEditing ? (
                            <select
                              className="w-full border rounded px-2 py-1"
                              value={normalizeId(tpl.clientId) || ''}
                              onChange={(e) =>
                                handleFieldChange(normalizeId(tpl), 'clientId', e.target.value)
                              }
                            >
                              <option value="">Sélectionner un client</option>
                              {clients.map((c) => (
                                <option key={normalizeId(c)} value={normalizeId(c)}>
                                  {c.companyName || c.name || 'Client'}
                                </option>
                              ))}
                            </select>
                          ) : (
                            getClientName(tpl.clientId)
                          )}
                        </td>

                        <td className="p-3 border">
                          {isEditing ? (
                            <select
                              className="w-full border rounded px-2 py-1"
                              value={normalizeId(tpl.defaultAssignee) || ''}
                              onChange={(e) =>
                                handleFieldChange(
                                  normalizeId(tpl),
                                  'defaultAssignee',
                                  e.target.value
                                )
                              }
                            >
                              <option value="">Sélectionner</option>
                              {collaborators.map((u) => (
                                <option key={normalizeId(u)} value={normalizeId(u)}>
                                  {u.name || u.email}
                                </option>
                              ))}
                            </select>
                          ) : (
                            getUserName(tpl.defaultAssignee)
                          )}
                        </td>

                        <td className="p-3 border">
                          {isEditing ? (
                            <select
                              className="w-full border rounded px-2 py-1"
                              value={tpl.frequency || 'monthly'}
                              onChange={(e) =>
                                handleFieldChange(normalizeId(tpl), 'frequency', e.target.value)
                              }
                            >
                              {FREQUENCIES.map((f) => (
                                <option key={f.value} value={f.value}>
                                  {f.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            FREQUENCIES.find((f) => f.value === tpl.frequency)?.label ||
                            tpl.frequency
                          )}
                        </td>

                        <td className="p-3 border">
                          {isEditing ? (
                            <input
                              type="date"
                              className="w-full border rounded px-2 py-1"
                              value={tpl.startDate ? String(tpl.startDate).slice(0, 10) : ''}
                              onChange={(e) =>
                                handleFieldChange(normalizeId(tpl), 'startDate', e.target.value)
                              }
                            />
                          ) : tpl.startDate ? (
                            new Date(tpl.startDate).toLocaleDateString('fr-FR')
                          ) : (
                            'N/A'
                          )}
                        </td>

                        <td className="p-3 border">
                          {isEditing ? (
                            <input
                              type="date"
                              className="w-full border rounded px-2 py-1"
                              value={tpl.endDate ? String(tpl.endDate).slice(0, 10) : ''}
                              onChange={(e) =>
                                handleFieldChange(normalizeId(tpl), 'endDate', e.target.value)
                              }
                            />
                          ) : tpl.endDate ? (
                            new Date(tpl.endDate).toLocaleDateString('fr-FR')
                          ) : (
                            '—'
                          )}
                        </td>

                        <td className="p-3 border">
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              className="w-full border rounded px-2 py-1"
                              value={tpl.estimatedHours ?? 0}
                              onChange={(e) =>
                                handleFieldChange(normalizeId(tpl), 'estimatedHours', e.target.value)
                              }
                            />
                          ) : (
                            `${tpl.estimatedHours || 0} h`
                          )}
                        </td>

                        <td className="p-3 border">
                          <button
                            onClick={() => handleToggleActive(tpl)}
                            className={`px-2 py-1 rounded text-xs ${
                              tpl.active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {tpl.active ? 'Actif' : 'Inactif'}
                          </button>
                        </td>

                        <td className="p-3 border">
                          {isEditing ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSave(tpl)}
                                className="px-3 py-1 bg-blue-600 text-white text-xs rounded"
                              >
                                Enregistrer
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="px-3 py-1 bg-gray-300 text-xs rounded"
                              >
                                Annuler
                              </button>

                              {/* 🗑️ Supprimer */}
                              <button
                                onClick={() => handleDeleteTemplate(tpl)}
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded"
                                title="Supprimer"
                              >
                                <Icon name="IconTrash" /> Supprimer
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingId(normalizeId(tpl))}
                                className="px-3 py-1 bg-white border text-xs rounded hover:bg-gray-100"
                              >
                                Modifier
                              </button>

                              {/* 🗑️ Supprimer */}
                              <button
                                onClick={() => handleDeleteTemplate(tpl)}
                                className="px-3 py-1 bg-white border border-red-300 text-red-700 text-xs rounded hover:bg-red-50"
                                title="Supprimer"
                              >
                                <Icon name="IconTrash" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {perClientTemplates.length === 0 && (
                    <tr>
                      <td className="p-4 text-center text-gray-500" colSpan={9}>
                        Aucun modèle client pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal programmation */}
          <AssignTemplatesToClient
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            onSuccess={loadData}
          />
        </>
      )}

      {/* TAB: Scheduled */}
      {activeTab === 'scheduled' && (
        <div className="space-y-4">
          <div className="bg-white border rounded-lg shadow p-4">
            <label className="block text-sm font-medium mb-2">Sélectionner un client</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              <option value="">-- Choisir --</option>
              {clients.map((c) => (
                <option key={normalizeId(c)} value={normalizeId(c)}>
                  {c.companyName || c.name || 'Client'}
                </option>
              ))}
            </select>

            {selectedClientObj && (
              <div className="mt-3 text-sm text-gray-700">
                Client sélectionné :{' '}
                <span className="font-semibold">{getClientName(selectedClientObj)}</span>
              </div>
            )}
          </div>

          {loadingScheduled ? (
            <div className="p-4">Chargement des données programmées...</div>
          ) : !selectedClientId ? (
            <div className="p-4 text-gray-600">Choisis un client pour voir ses modèles et tâches.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white border rounded-lg shadow overflow-x-auto">
                <div className="p-3 border-b font-semibold">Modèles assignés au client</div>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-3 border text-left">Nom</th>
                      <th className="p-3 border text-left">Collaborateur</th>
                      <th className="p-3 border text-left">Fréquence</th>
                      <th className="p-3 border text-left">Actif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientTemplates.map((tpl) => (
                      <tr key={normalizeId(tpl)} className="hover:bg-gray-50">
                        <td className="p-3 border">
                          <div className="font-medium">{tpl.name}</div>
                          <div className="text-xs text-gray-500">{tpl.description}</div>
                        </td>
                        <td className="p-3 border">{getUserName(tpl.defaultAssignee)}</td>
                        <td className="p-3 border">
                          {FREQUENCIES.find((f) => f.value === tpl.frequency)?.label || tpl.frequency}
                        </td>
                        <td className="p-3 border">{tpl.active ? 'Oui' : 'Non'}</td>
                      </tr>
                    ))}
                    {clientTemplates.length === 0 && (
                      <tr>
                        <td className="p-4 text-center text-gray-500" colSpan={4}>
                          Aucun modèle assigné à ce client.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="bg-white border rounded-lg shadow overflow-x-auto">
                <div className="p-3 border-b font-semibold">Tâches générées (récurrentes)</div>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-3 border text-left">Titre</th>
                      <th className="p-3 border text-left">Échéance</th>
                      <th className="p-3 border text-left">Assigné</th>
                      <th className="p-3 border text-left">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledTasks.map((t) => (
                      <tr key={normalizeId(t)} className="hover:bg-gray-50">
                        <td className="p-3 border">{t.title}</td>
                        <td className="p-3 border">
                          {t.dueDate ? new Date(t.dueDate).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="p-3 border">{getUserName(t.assignedTo)}</td>
                        <td className="p-3 border">{t.status || 'pending'}</td>
                      </tr>
                    ))}
                    {scheduledTasks.length === 0 && (
                      <tr>
                        <td className="p-4 text-center text-gray-500" colSpan={4}>
                          Aucune tâche récurrente générée pour ce client (pour l’instant).
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
